// „Bilet nou din JSON” (Întrebări → Unelte, la examen): întrebările dintr-un fișier
// JSON devin întrebări noi (POST /exams/questions), apoi biletul ales primește exact
// id-urile create. Fost ExamTicketsMonitor (pagina Bilete examen). Răspunsul corect e
// de la 1, ca la toate biletele de examen.

import {
   parseJsonArraySafe,
   parseLocalizedAnswerRaw,
   parseLocalizedTextRaw,
   stringifyLocalizedAnswer,
   toPositiveInt,
} from "./examQuestionFormat";

const MIN_ANSWERS = 2;

function isObject(value) {
   return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function firstNonEmpty(...values) {
   for (const value of values) {
      const text = String(value ?? "").trim();
      if (text) return text;
   }
   return "";
}

function normalizeLooseKey(value) {
   return String(value ?? "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");
}

function getValueByLooseKeys(obj, keys = []) {
   if (!isObject(obj)) return undefined;
   const normalized = keys.map((k) => normalizeLooseKey(k));

   for (const [key, value] of Object.entries(obj)) {
      if (normalized.includes(normalizeLooseKey(key))) {
         return value;
      }
   }

   return undefined;
}

function inferCorrectAnswerFromAnswerFlags(answersBase = []) {
   for (let i = 0; i < answersBase.length; i += 1) {
      const answer = answersBase[i];
      if (!isObject(answer)) continue;

      const flag = answer.correct ?? answer.isCorrect ?? answer.right ?? answer.ok ?? answer.valid ?? false;

      if (flag === true) return i + 1;
   }

   return null;
}

function normalizeImportCorrectAnswer(rawValue, answersCount) {
   const text = String(rawValue ?? "").trim();
   if (!text) return null;

   let n = null;

   if (/^[+-]?\d+$/.test(text)) {
      n = Number(text);
   } else {
      const upper = text.toUpperCase();

      if (/^[A-F]$/.test(upper)) {
         n = upper.charCodeAt(0) - 64;
      } else {
         const romanMap = { I: 1, II: 2, III: 3, IV: 4, V: 5, VI: 6 };
         if (Object.prototype.hasOwnProperty.call(romanMap, upper)) {
            n = romanMap[upper];
         } else {
            const nums = text.match(/\d+/g);
            if (nums?.length === 1) {
               n = Number(nums[0]);
            }
         }
      }
   }

   if (!Number.isInteger(n)) return null;
   if (n >= 1 && n <= answersCount) return n; // de la 1
   if (n >= 0 && n < answersCount) return n + 1; // de la 0 → de la 1
   return null;
}

/** Imaginea întrebării: /EXAMEN_INSTRUIRE_AUTO/images/B{bilet}/image{ordine}.jpeg */
export function buildExamImagePath(ticketNo, orderNo) {
   const ticket = toPositiveInt(ticketNo);
   const order = toPositiveInt(orderNo);
   if (!ticket || !order) return null;
   return `/EXAMEN_INSTRUIRE_AUTO/images/B${ticket}/image${order}.jpeg`;
}

/** Numărul biletului din nume („Bilet 12”, „B12”, „Biletul 12”), pentru imagini. */
export function guessTicketNumberFromName(value) {
   const text = String(value ?? "").trim();
   if (!text) return null;

   const m1 = text.match(/\bB(?:ilet)?\s*#?\s*(\d+)\b/i);
   if (m1) return toPositiveInt(m1[1]);

   const m2 = text.match(/\bBiletul\s*#?\s*(\d+)\b/i);
   if (m2) return toPositiveInt(m2[1]);

   const m3 = text.match(/\b(\d+)\b/);
   if (m3) return toPositiveInt(m3[1]);

   return null;
}

function normalizeImportQuestion(raw, index) {
   if (!isObject(raw)) {
      throw new Error(`Întrebarea #${index + 1} nu este un obiect.`);
   }

   const sourceId = toPositiveInt(raw?.id) ?? index + 1;
   const order = toPositiveInt(raw?.order) ?? index + 1;

   const textLocalized = parseLocalizedTextRaw(raw?.text);
   const textRo = firstNonEmpty(raw?.textRo, raw?.text_ro, raw?.textRO, textLocalized.ro);
   const textRu = firstNonEmpty(raw?.textRu, raw?.text_ru, raw?.textRU, textLocalized.ru);

   if (!textRo && !textRu) {
      throw new Error(`Întrebarea #${order}: lipsește textul (text / textRu).`);
   }

   const answersBase = parseJsonArraySafe(raw?.answers, []);
   const answersRuBase = parseJsonArraySafe(raw?.answersRu ?? raw?.answersRU ?? raw?.answers_ru, []);

   if (!answersBase.length && !answersRuBase.length) {
      throw new Error(`Întrebarea #${order}: lipsesc răspunsurile.`);
   }

   const maxAnswerSource = Math.max(answersBase.length, answersRuBase.length);
   const answers = [];

   for (let i = 0; i < maxAnswerSource; i += 1) {
      const roObj = parseLocalizedAnswerRaw(answersBase[i]);
      const ruObj = parseLocalizedAnswerRaw(answersRuBase[i]);

      const ro = firstNonEmpty(roObj?.ro, roObj?.ru);
      const ru = firstNonEmpty(ruObj?.ru, ruObj?.ro, roObj?.ru);

      if (!ro && !ru) continue;

      answers.push({ ro: ro || ru, ru: ru || ro });
   }

   if (answers.length < MIN_ANSWERS) {
      throw new Error(`Întrebarea #${order}: sunt necesare minimum ${MIN_ANSWERS} răspunsuri.`);
   }

   const rawCorrectAnswerFromKeys =
      getValueByLooseKeys(raw, [
         "correctAnswer",
         "correct_answer",
         "correctIndex",
         "correct_index",
         "rightIndex",
         "right_index",
         "answerIndex",
         "answer_index",
         "rightAnswer",
         "right_answer",
         "goodAnswer",
         "good_answer",
         "solution",
         "answer",
      ]) ?? null;

   const rawCorrectAnswer =
      rawCorrectAnswerFromKeys ??
      inferCorrectAnswerFromAnswerFlags(answersBase) ??
      getValueByLooseKeys(raw, ["selectedAnswer", "selected_answer"]) ??
      null;

   const parsedCorrectAnswer = normalizeImportCorrectAnswer(rawCorrectAnswer, answers.length);
   const correctAnswer = parsedCorrectAnswer || 1;

   const categoryId = raw?.categoryId == null ? null : (toPositiveInt(raw?.categoryId) ?? null);

   return {
      source: {
         id: sourceId,
         order,
         text: textRo,
         textRu,
         image: firstNonEmpty(raw?.image) || null,
         answers: answers.map((a) => a.ro),
         answersRu: answers.map((a) => a.ru),
         correctAnswer: rawCorrectAnswer,
         correctAnswerFallbackUsed: !parsedCorrectAnswer,
         categoryId: raw?.categoryId ?? null,
      },
      payload: {
         order,
         text: JSON.stringify({ ro: textRo, ru: textRu }),
         answers: answers.map((answer) => stringifyLocalizedAnswer(answer)),
         correctAnswer,
         categoryId,
      },
   };
}

/** Întrebările din fișier (un array sau { questions | items | rows | data }), ordinile unice. */
export function parseNewTicketImport(parsed) {
   const root = isObject(parsed) ? parsed : {};

   const rootItems = Array.isArray(parsed)
      ? parsed
      : parseJsonArraySafe(root?.questions, null) ||
        parseJsonArraySafe(root?.items, null) ||
        parseJsonArraySafe(root?.rows, null) ||
        parseJsonArraySafe(root?.data?.questions, null) ||
        parseJsonArraySafe(root?.data?.items, null);

   if (!Array.isArray(rootItems)) {
      throw new Error('JSON invalid. Aștept un array de întrebări sau un obiect cu "questions".');
   }

   const items = rootItems
      .map((item, idx) => normalizeImportQuestion(item, idx))
      .sort((a, b) => Number(a.payload.order) - Number(b.payload.order));

   const usedOrders = new Set();

   const normalizedUnique = items.map((item, idx) => {
      const next = { ...item, source: { ...item.source }, payload: { ...item.payload } };

      let nextOrder = toPositiveInt(next?.payload?.order);
      if (!nextOrder || usedOrders.has(nextOrder)) {
         nextOrder = idx + 1;
      }

      usedOrders.add(nextOrder);
      next.payload.order = nextOrder;
      next.source.order = nextOrder;

      return next;
   });

   return {
      items: normalizedUnique,
      correctFallbackCount: normalizedUnique.filter((item) => item?.source?.correctAnswerFallbackUsed).length,
   };
}

/** Id-ul întrebării create, oricum îl întoarce serverul. */
export function extractCreatedQuestionId(raw) {
   return (
      toPositiveInt(raw?.id) ??
      toPositiveInt(raw?.question?.id) ??
      toPositiveInt(raw?.data?.id) ??
      toPositiveInt(raw?.data?.question?.id) ??
      null
   );
}
